<template>
  <div v-if="loading" class="loading-container">
    <v-progress-circular color="#ffd139" indeterminate :size="64" :width="6"></v-progress-circular>
    <div>{{ $t('i18n.preparingContent') }}</div>
  </div>
  <div v-else class="view-container">
    <MainNavBar @navigate="changeActiveTab" />
    <div class="content-container">
      <RouterView />
    </div>
    <FooterBar />
    <MobileMainNavBar />
  </div>

  <div class="alerts-container">
    <FeedbackAlert v-for="alert in alerts" :key="alert.id" :text="alert.text" :feedbackType="alert.feedbackType"
      @close-alert="removeAlert(alert.id)" />
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import router from './router'
import { useAppStore } from './stores/appStore'
import { timers } from './utils/HelperFunctions'
import MainNavBar from './components/navbars/MainNavBar.vue'
import MobileMainNavBar from './components/navbars/MobileMainNavBar.vue'
import FeedbackAlert from './components/misc/FeedbackAlert.vue'
import FooterBar from './components/misc/FooterBar.vue'
import { login, refreshTokenPair } from './utils/keyCloak'

export default defineComponent({
  name: 'App',
  components: {
    MainNavBar,
    MobileMainNavBar,
    FeedbackAlert,
    FooterBar
  },
  async beforeMount () {
    const appStore = useAppStore()
    try {
      await router.isReady()

      const authenticated = await login()

      if (authenticated) {
        this.initializeAccessTokenRefresher()
        await this.getConfigurationData()
        return
      }

      router.push({ name: 'dashboard' })
    } catch (error) {
      appStore.clearSession()
      console.error('An error occured during app mounting: ', error)
    } finally {
      this.loading = false
    }
  },
  async beforeUnmount () {
    const appStore = useAppStore()
    this.clearTokenRefresherTimer()
    appStore.clearSession()
  },
  data () {
    return {
      activeTabName: 'dashboard',
      loading: true,
      refreshTokenPollingTimer: undefined as ReturnType<typeof setInterval> | undefined
    }
  },
  methods: {
    changeActiveTab (tabName: string) {
      this.activeTabName = tabName
    },
    initializeAccessTokenRefresher () {
      try {
        console.log('Initializing token pair refresh timer...')
        timers.start('refreshTokenPair', refreshTokenPair, 5 * 1000)
        console.log('Initialized.')
      } catch (error) {
        console.error('Error in access token refresher setup: ', error)
      }
    },
    clearTokenRefresherTimer () {
      timers.stop('refreshTokenPair')
    },
    getConfigurationData () {
      // Fetch config data from backend
    },
    removeAlert (alertId: string) {
      const appStore = useAppStore()
      appStore.removeAlert(alertId)
    }
  },
  computed: {
    alerts () {
      const appStore = useAppStore()
      return appStore.alerts
    }
  }
})
</script>

<style lang="scss">
@import "./styles/base.scss";
@import "./styles/fonts.scss";
@import "./styles/colors.scss";

html,
body,
#app {
  height: 100%;
  overflow-y: hidden;
}

.alerts-container {
  position: fixed;
  bottom: 6em;
  right: 2.5em;
  z-index: 9999;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 1em;
  max-width: 100%;
}

@media (max-width: 960px) {
  .alerts-container {
    left: 50%;
    right: auto;
    transform: translateX(-50%);
    min-width: 25em;
  }
}

.view-container {
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;

  .content-container {
    background-color: $grey-3;
    flex: 1; // Make this container take up remaining space between the navbar and footer
    height: 100%;
    overflow: auto;
  }
}

.loading-container {
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2em;
  /* background-color: aqua; */
}
</style>
